import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import MonacoEditor, {
  Monaco,
  OnMount,
  BeforeMount,
} from '@monaco-editor/react';
import { editor as monacoEditor } from 'monaco-editor';
import { cn } from '../../lib/utils';
import { useColorMode } from '../../contexts/ColorModeContext';
import { SchemaProvider } from './autocomplete/types';
import { setupSimpleSql } from './language/monaco-sql-simple';
import { databricksSqlTheme, databricksSqlDarkTheme } from './databricksTheme';
import { TableNameHighlighter } from './language/tableHighlighter';
import { shouldIgnoreQueryForAntlrParsing } from './language/antlrUtils';
import SyntaxWorker from './language/syntaxChecker.worker?worker';
import './sqlEditor.css';

export interface DatabricksSqlEditorProps {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  height?: string;
  theme?: 'light' | 'dark';
  readOnly?: boolean;
  className?: string;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  schemaProvider?: SchemaProvider;
  name?: string; // For form field identification
  syntaxValidation?: 'antlr' | 'none'; // Choose validation method
}

export interface DatabricksSqlEditorRef {
  getValue: () => string;
  setValue: (value: string) => void;
  focus: () => void;
}

export const DatabricksSqlEditor = forwardRef<
  DatabricksSqlEditorRef,
  DatabricksSqlEditorProps
>(
  (
    {
      value = '',
      onChange,
      placeholder,
      height = '200px',
      theme,
      readOnly = false,
      className,
      onBlur,
      onFocus,
      schemaProvider,
      name,
      syntaxValidation = 'antlr',
    },
    ref,
  ) => {
    const editorRef = useRef<monacoEditor.IStandaloneCodeEditor | null>(null);
    const monacoRef = useRef<Monaco | null>(null);
    const tableHighlighter = useRef<TableNameHighlighter | null>(null);
    const syntaxWorkerRef = useRef<Worker | null>(null);
    const { isDarkMode } = useColorMode();

    // Use explicit theme prop if provided, otherwise use color mode context
    const isDark = theme ? theme === 'dark' : isDarkMode;

    // Initialize Web Worker for syntax checking
    useEffect(() => {
      if (syntaxValidation === 'antlr') {
        syntaxWorkerRef.current = new SyntaxWorker();

        return () => {
          syntaxWorkerRef.current?.terminate();
          syntaxWorkerRef.current = null;
        };
      }
    }, [syntaxValidation]);
    const options = useMemo(() => {
      return {
        readOnly,
        minimap: { enabled: false },
        fontSize: 14,
        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        automaticLayout: true,
        lineNumbers: 'on',
        lineDecorationsWidth: 0,
        lineNumbersMinChars: 3,
        folding: true,
        quickSuggestions: true,
        suggestOnTriggerCharacters: true,
        // Ensure hover is enabled
        hover: {
          enabled: true,
          delay: 300,
          sticky: true,
        },
        // Ensure we can see overflow content (tooltips)
        fixedOverflowWidgets: true,
      } as monacoEditor.IStandaloneEditorConstructionOptions;
    }, [readOnly]);

    useImperativeHandle(ref, () => ({
      getValue: () => editorRef.current?.getValue() || '',
      setValue: (newValue: string) => {
        editorRef.current?.setValue(newValue);
      },
      focus: () => editorRef.current?.focus(),
    }));

    useEffect(() => {
      return () => {
        if (editorRef.current) {
          tableHighlighter.current?.clear(editorRef.current);
          editorRef.current.dispose?.();
        }
      };
    }, []);

    const createSyntheticEvent = useCallback(
      (
        type: 'change' | 'focus' | 'blur',
        valueStr: string,
      ): React.ChangeEvent<HTMLTextAreaElement> &
        React.FocusEvent<HTMLTextAreaElement> => {
        const target = {
          value: valueStr,
          name: name || '',
        } as unknown as HTMLTextAreaElement;

        return {
          target,
          currentTarget: target,
          type,
          bubbles: true,
          cancelable: true,
          preventDefault: () => {},
          stopPropagation: () => {},
          persist: () => {},
        } as React.ChangeEvent<HTMLTextAreaElement> &
          React.FocusEvent<HTMLTextAreaElement>;
      },
      [name],
    );

    const handleBeforeMount: BeforeMount = (monaco) => {
      // Register the custom themes before the editor mounts
      monaco.editor.defineTheme('databricks-light', databricksSqlTheme);
      monaco.editor.defineTheme('databricks-dark', databricksSqlDarkTheme);

      // Setup simplified SQL language configurations
      setupSimpleSql(monaco, schemaProvider);
    };

    const handleEditorDidMount: OnMount = (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      // Initialize table name highlighter
      tableHighlighter.current = new TableNameHighlighter();

      // Update decorations on content change
      const updateDecorations = () => {
        if (tableHighlighter.current) {
          tableHighlighter.current.updateDecorations(editor);
        }
      };

      // Initial decoration
      updateDecorations();

      // Setup ANTLR syntax validation
      const updateSyntaxValidation = () => {
        if (
          syntaxValidation === 'antlr' &&
          monacoRef.current &&
          syntaxWorkerRef.current
        ) {
          const model = editor.getModel();
          if (model) {
            const sqlText = model.getValue();

            // Skip validation for ignored queries
            if (shouldIgnoreQueryForAntlrParsing(sqlText)) {
              monacoRef.current.editor.setModelMarkers(model, 'sql-syntax', []);
              return;
            }

            // Send to Web Worker for processing
            syntaxWorkerRef.current.postMessage({ statement: sqlText });

            // Handle worker response
            syntaxWorkerRef.current.onmessage = (event) => {
              const { syntaxError, isValid } = event.data;

              // Debug logging (dev only)
              if (process.env.NODE_ENV === 'development') {
                console.warn('ANTLR validation result from worker:', {
                  isValid,
                  syntaxError,
                  sqlText,
                });
              }

              if (
                syntaxError &&
                monacoRef.current &&
                model.isAttachedToEditor()
              ) {
                const markers: monacoEditor.IMarkerData[] = [
                  {
                    severity: monacoRef.current.MarkerSeverity.Error,
                    message: `Syntax error: unexpected "${syntaxError.token}"${
                      syntaxError.expected.length > 0
                        ? `. Did you mean: ${syntaxError.expected[0].text}?`
                        : ''
                    }`,
                    startLineNumber: syntaxError.loc.first_line,
                    startColumn: syntaxError.loc.first_column,
                    endLineNumber: syntaxError.loc.last_line,
                    endColumn: syntaxError.loc.last_column,
                  },
                ];

                monacoRef.current.editor.setModelMarkers(
                  model,
                  'sql-syntax',
                  markers,
                );
              } else if (monacoRef.current && model.isAttachedToEditor()) {
                monacoRef.current.editor.setModelMarkers(
                  model,
                  'sql-syntax',
                  [],
                );
              }
            };
          }
        }
      };

      // Debounce syntax validation for better performance
      let validationTimeout: NodeJS.Timeout;
      const debouncedSyntaxValidation = () => {
        clearTimeout(validationTimeout);
        validationTimeout = setTimeout(() => {
          updateSyntaxValidation();
        }, 300); // Wait 300ms after user stops typing
      };

      // Initial syntax validation
      updateSyntaxValidation();

      // Setup focus/blur handlers
      editor.onDidFocusEditorWidget(() => {
        if (onFocus) {
          onFocus(createSyntheticEvent('focus', editor.getValue()));
        }
      });

      editor.onDidBlurEditorWidget(() => {
        if (onBlur) {
          onBlur(createSyntheticEvent('blur', editor.getValue()));
        }
      });

      // Add placeholder widget
      let placeholderWidget: monacoEditor.IContentWidget | null = null;

      const updatePlaceholder = () => {
        const currentValue = editor.getValue();

        if (!currentValue && placeholder) {
          if (!placeholderWidget) {
            placeholderWidget = {
              getId: () => 'editor.placeholder',
              getDomNode: () => {
                const node = document.createElement('div');
                node.style.opacity = '0.5';
                node.style.fontFamily =
                  'Monaco, Consolas, "Courier New", monospace';
                node.style.fontSize = '14px';
                node.style.pointerEvents = 'none';
                node.style.whiteSpace = 'pre';
                node.className = isDark ? 'text-gray-400' : 'text-gray-500';
                node.textContent = placeholder;
                return node;
              },
              getPosition: () => ({
                position: { lineNumber: 1, column: 1 },
                preference: [
                  monacoEditor.ContentWidgetPositionPreference.EXACT,
                ],
              }),
            };
            editor.addContentWidget(placeholderWidget);
          }
        } else if (placeholderWidget) {
          editor.removeContentWidget(placeholderWidget);
          placeholderWidget = null;
        }
      };

      // Initial placeholder check
      updatePlaceholder();

      // Update decorations and placeholder on content change
      editor.onDidChangeModelContent(() => {
        updateDecorations();
        updatePlaceholder();
        debouncedSyntaxValidation();
      });

      // Force a layout update after mount
      setTimeout(() => {
        editor.layout();
      }, 100);

      // Clean up timeout on unmount
      return () => {
        clearTimeout(validationTimeout);
      };
    };

    const handleChange = useCallback(
      (newValue: string | undefined) => {
        if (onChange) {
          onChange(createSyntheticEvent('change', newValue || ''));
        }
      },
      [onChange, createSyntheticEvent],
    );

    return (
      <div
        className={cn(
          'sql-editor-container relative overflow-hidden rounded-sm',
          className,
        )}
        style={{ height: height === '100%' ? '100%' : height, width: '100%' }}
      >
        <MonacoEditor
          height="100%"
          width="100%"
          language="databricks-sql"
          value={value}
          onChange={handleChange}
          beforeMount={handleBeforeMount}
          onMount={handleEditorDidMount}
          options={options}
          theme={isDark ? 'databricks-dark' : 'databricks-light'}
        />
      </div>
    );
  },
);

DatabricksSqlEditor.displayName = 'DatabricksSqlEditor';
